import { Fancybox } from '@fancyapps/ui';
import { formatMoney } from '@shopify/theme-currency';

// Products that combine size into separate dimension options (instead of one
// "Size"/"Maat" option) get matched against these, in this order, to build an
// equivalent "WIDTHxLENGTH" value so size filtering/matching still works.
const SIZE_OPTION_REGEX = /^(size|maat)$/i;
const DIMENSION_OPTION_REGEXES = [/^(width|breedte)$/i, /^(length|lengte)$/i];

const STEP_CONFIG = {
  1: {
    type: 'single-product',
    hideNonExistentCombos: (optionIndex) => optionIndex > 0, // First option always visible
    hideUnavailable: false,
    greyOutUnavailable: true,
  },
  2: {
    type: 'multi-product',
    showQualitySelector: true,
    hideNonExistentCombos: () => false,
    hideUnavailable: true,
    greyOutUnavailable: false,
    skipOptions: ['borduring'],
  },
  3: {
    type: 'multi-product',
    hideNonExistentCombos: () => false,
    hideUnavailable: false,
    greyOutUnavailable: true,
  },
};

class PillowConfigurator extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-configurator-data]');
    if (!dataEl) return;

    this.config = JSON.parse(dataEl.textContent);
    this.state = {
      currentStep: 1,
      selections: {},
      qualityFilter: null,
    };
    this._isUpdating = false;
    this._hasTrackedStart = false;

    this.elements = {
      addAllBtn: this.querySelector('[data-add-all-to-cart]'),
      total: this.querySelector('[data-total]'),
      images: this.querySelector('[data-product-images]'),
      progress: this.querySelector('[data-progress]'),
      bundleOverview: this.querySelector('[data-bundle-overview]'),
    };

    this.restoreFromURL();
    this.applyDefaultVariants();
    this.render();
    this.attachListeners();
    this.initializeSelections();
    this.openStep(this.state.currentStep);
    this.updateUI();
    this.initFancybox();
  }

  initializeSelections() {
    this.config.steps.forEach((step) => {
      if (this.state.selections[step.number] !== undefined) {
        this.syncButtonsToSelection(step.number);
        this.updateButtonStates(step.number);
      } else if (step.number === this.state.currentStep) {
        this.selectFromActiveButtons(step.number);
      } else {
        this.updateButtonStates(step.number);
      }
    });
  }

  getStep(n) {
    return this.config.steps.find((s) => s.number === n);
  }

  get sizeFilter() {
    return this.state.selections[1]?.size || null;
  }

  getStepConfig(stepNum) {
    return STEP_CONFIG[stepNum] || STEP_CONFIG[3];
  }

  restoreFromURL() {
    const params = new URLSearchParams(window.location.search);
    const step = parseInt(params.get('step'));
    if (step >= 1 && step <= this.config.steps.length) {
      this.state.currentStep = step;
    }

    this.config.steps.forEach(({ number }) => {
      const paramValue = params.get(`s${number}`);
      if (!paramValue) return;

      // Handle "none" selection
      if (paramValue === 'none') {
        this.state.selections[number] = null;
        return;
      }

      const variantId = parseInt(paramValue);
      if (!variantId) return;

      const match = this.findVariantById(number, variantId);
      if (match) {
        this.state.selections[number] = this.createSelection(match);
      }
    });
  }

  applyDefaultVariants() {
    const defaults = window.pillowConfiguratorSettings?.defaultVariants || {};

    this.config.steps.forEach(({ number }) => {
      if (this.state.selections[number] !== undefined) return;

      const defaultVariantId = parseInt(defaults[number]);
      if (!defaultVariantId) return;

      const match = this.findVariantById(number, defaultVariantId);
      if (match) {
        this.state.selections[number] = this.createSelection(match);
      }
    });
  }

  persistToURL() {
    const params = new URLSearchParams();
    params.set('step', this.state.currentStep);
    Object.entries(this.state.selections).forEach(([step, sel]) => {
      // Handle "none" selections (stored as null)
      if (sel === null) {
        params.set(`s${step}`, 'none');
      } else if (sel) {
        params.set(`s${step}`, sel.variantId);
      }
    });
    history.replaceState({}, '', `${location.pathname}?${params}`);
  }

  findVariantById(stepNum, variantId) {
    const step = this.getStep(stepNum);
    for (const product of step?.products || []) {
      const variant = product.variants.find((v) => v.id === variantId);
      if (variant) return { product, variant };
    }
    return null;
  }

  getVariantOptions(product, variant) {
    return Object.fromEntries(product.options.map((name, i) => [name, variant.options[i]]));
  }

  extractSize(product, variant) {
    const idx = product.options.findIndex((o) => SIZE_OPTION_REGEX.test(o));
    if (idx > -1) return variant.options[idx];
    return this.extractCombinedSize(product, variant);
  }

  // Some products (e.g. mattresses) don't have one combined "Size" option,
  // but separate dimension options like Width/Length instead. Combine the
  // numeric parts of those into the same "WIDTHxLENGTH" format used by
  // normalizeSize(), so a mattress's Width+Length still matches a fitted
  // sheet's single Size option.
  extractCombinedSize(product, variant) {
    const parts = DIMENSION_OPTION_REGEXES.map((regex) => {
      const idx = product.options.findIndex((o) => regex.test(o));
      return idx > -1 ? this.extractNumber(variant.options[idx]) : null;
    });

    return parts.every(Boolean) ? parts.join('x') : null;
  }

  extractNumber(value) {
    return value?.match(/\d+/)?.[0] || null;
  }

  normalizeSize(size) {
    return size?.match(/(\d+x\d+)/i)?.[1]?.toLowerCase() || null;
  }

  sizesMatch(a, b) {
    const na = this.normalizeSize(a);
    const nb = this.normalizeSize(b);
    return na && nb && na === nb;
  }

  matchesSizeFilter(product, variant) {
    if (!this.sizeFilter) return true;
    return this.sizesMatch(this.extractSize(product, variant), this.sizeFilter);
  }

  variantMatchesOptions(product, variant, options) {
    const opts = this.getVariantOptions(product, variant);
    return Object.entries(options).every(([key, val]) => {
      if (key === 'product' || key === 'quality') return true;
      if (key === 'size') return this.sizesMatch(this.extractSize(product, variant), val);
      return opts[key] === val;
    });
  }

  createSelection({ product, variant }) {
    return {
      productId: product.id,
      variantId: variant.id,
      title: product.title,
      variantTitle: variant.title,
      price: variant.price,
      compareAtPrice: variant.compare_at_price,
      image: product.featured_image?.src || '',
      size: this.extractSize(product, variant),
      available: variant.available,
    };
  }

  getFilteredProducts(stepNum, { skipQualityFilter = false } = {}) {
    const step = this.getStep(stepNum);
    let products = step?.products || [];

    // Size filter (steps > 1)
    if (stepNum > 1 && this.sizeFilter) {
      products = products.filter((p) => p.variants.some((v) => this.matchesSizeFilter(p, v)));
    }

    // Quality filter (step 2 only)
    if (stepNum === 2 && this.state.qualityFilter && !skipQualityFilter) {
      products = products.filter((p) => {
        const q = p.pillowcase_quality;
        if (this.state.qualityFilter === window.pillowConfiguratorSettings.other_category) {
          return !Array.isArray(q) || q.length === 0;
        }
        return Array.isArray(q) && q.includes(this.state.qualityFilter);
      });
    }

    return products;
  }

  getUniqueQualities(stepNum) {
    const products = this.getFilteredProducts(stepNum, { skipQualityFilter: true });
    const qualities = new Set();
    let hasNull = false;

    products.forEach((p) => {
      const q = p.pillowcase_quality;
      if (Array.isArray(q) && q.length) {
        q.forEach((v) => qualities.add(v));
      } else {
        hasNull = true;
      }
    });

    const sorted = [...qualities].sort();
    if (hasNull) sorted.push(window.pillowConfiguratorSettings.other_category);
    return sorted;
  }

  render() {
    this.config.steps.forEach((step) => this.renderStep(step));
    this.injectInfoIcons();
  }

  renderStep(step) {
    const container = this.querySelector(`[data-step-attributes="${step.number}"]`);
    if (!container) return;

    const config = this.getStepConfig(step.number);
    const products = this.getFilteredProducts(step.number);

    let html = '';

    // Quality selector (step 2)
    if (config.showQualitySelector) html += this.renderQualitySelector(step.number);

    // Product/option groups
    if (step.products.length === 1) {
      html += this.renderSingleProductOptions(step.products[0], step.number);
    } else if (products.length) {
      html += this.renderMultiProductOptions(products, step.number);
    }

    container.innerHTML = html;
    this.setDefaultActiveButtons(container);
  }

  renderQualitySelector(stepNum) {
    const qualities = this.getUniqueQualities(stepNum);
    if (qualities.length <= 1) return '';

    if (!this.state.qualityFilter) {
      this.state.qualityFilter = qualities[0];
    }

    const buttons = qualities
      .map(
        (q) => `
      <button type="button"
        class="configurator-attribute-button${this.state.qualityFilter === q ? ' active' : ''}"
        data-attr="quality" data-value="${q}" data-step="${stepNum}">
        ${q}
      </button>`
      )
      .join('');

    return `
      <div class="configurator-attribute-group" data-quality-selector>
        <h3 class="configurator-attribute-label">${window.pillowConfiguratorSettings.quality}</h3>
        <div class="configurator-attribute-buttons">${buttons}</div>
      </div>`;
  }

  renderSingleProductOptions(product, stepNum) {
    const config = this.getStepConfig(stepNum);
    return product.options
      .map((name, idx) => {
        if (config.skipOptions?.includes(name.toLowerCase())) return '';
        const values = [...new Set(product.variants.map((v) => v.options[idx]).filter(Boolean))];
        return this.renderOptionGroup(name, values, stepNum, idx);
      })
      .join('');
  }

  renderMultiProductOptions(products, stepNum) {
    const config = this.getStepConfig(stepNum);
    const useThumbnails = stepNum === 2;
    const settings = window.pillowConfiguratorSettings || {};
    const allowNone = (stepNum === 2 && settings.step2AllowNone) || (stepNum === 3 && settings.step3AllowNone);

    // Product selector
    const productButtons = products
      .map((p) => {
        const img = useThumbnails ? (p.swatch_image ? { src: p.swatch_image } : p.images?.[1] || p.featured_image) : null;
        const content = img ? `<img src="${img.src}" alt="${p.color_title || p.title}" loading="lazy"><span class="tooltip">${p.color_title || p.title}</span>` : p.title;
        return `
        <button type="button"
          class="configurator-attribute-button${img ? ' configurator-attribute-button--image' : ''}"
          data-attr="product" data-value="${p.id}" data-step="${stepNum}" title="${p.color_title || p.title}">
          ${content}
        </button>`;
      })
      .join('');

    let flexClass = '';
    if (useThumbnails == true) flexClass = 'configurator-attribute-buttons--flex';

    const productSelectorLabel = stepNum === 3 ? window.pillowConfiguratorSettings.step3ProductLabel : window.pillowConfiguratorSettings.step2ProductLabel;

    const productGroup = `
      <div class="configurator-attribute-group" data-product-selector>
      <h3 class="configurator-attribute-label">${productSelectorLabel}</h3>
        <div class="configurator-attribute-buttons ${flexClass}">${productButtons}</div>
      </div>`;

    // Collect all variant options
    const allOptions = {};
    products.forEach((p) => {
      p.options.forEach((name, i) => {
        if (config.skipOptions?.includes(name.toLowerCase())) return;
        if (SIZE_OPTION_REGEX.test(name)) return; // Size handled by step 1
        if (!allOptions[name]) allOptions[name] = new Set();
        p.variants.forEach((v) => v.options[i] && allOptions[name].add(v.options[i]));
      });
    });

    const optionGroups = Object.entries(allOptions)
      .map(([name, values]) => this.renderOptionGroup(name, [...values], stepNum))
      .join('');

    const noneButton = allowNone
      ? `
      <button type="button"
        class="configurator-none-button"
        data-attr="product" data-value="none" data-step="${stepNum}" data-none-selector>
        ${stepNum === 2 ? settings.step2NoneText : settings.step3NoneText}
      </button>`
      : '';

    return productGroup + optionGroups + noneButton;
  }

  renderOptionGroup(name, values, stepNum, optionIndex = null) {
    const isSize = SIZE_OPTION_REGEX.test(name);
    const attrName = isSize ? 'size' : name;

    const buttons = values
      .map(
        (val) => `
      <button type="button" class="configurator-attribute-button"
        data-attr="${attrName}" data-value="${val}" data-step="${stepNum}"
        ${optionIndex !== null ? `data-option-index="${optionIndex}"` : ''}>
        ${val}
      </button>`
      )
      .join('');

    const flexClass = isSize === true ? ' configurator-attribute-buttons--flex' : '';

    return `
      <div class="configurator-attribute-group" ${optionIndex !== null ? `data-option-index="${optionIndex}"` : ''}>
        <h3 class="configurator-attribute-label">${name}</h3>
        <div class="configurator-attribute-buttons ${flexClass}">${buttons}</div>
      </div>`;
  }

  setDefaultActiveButtons(container) {
    container.querySelectorAll('.configurator-attribute-group').forEach((group) => {
      if (group.dataset.qualitySelector !== undefined) return;
      const firstBtn = group.querySelector('.configurator-attribute-button');
      firstBtn?.classList.add('active');
    });
  }

  injectInfoIcons() {
    const infoBlocks = window.pillowConfiguratorInfoBlocks || [];
    if (!infoBlocks.length) return;

    this.querySelectorAll('.configurator-attribute-label').forEach((label) => {
      const text = label.textContent.trim();
      const block = infoBlocks.find((b) => b.label.toLowerCase() === text.toLowerCase());
      if (!block || label.querySelector('info-dialog-trigger')) return;

      const trigger = document.createElement('info-dialog-trigger');
      trigger.className = 'configurator-info-button';
      trigger.dataset.infoTrigger = block.label;
      trigger.textContent = '\u24d8';
      label.appendChild(trigger);
    });
  }

  updateButtonStates(stepNum) {
    const container = this.querySelector(`[data-step-attributes="${stepNum}"]`);
    if (!container) return;

    const config = this.getStepConfig(stepNum);
    const products = this.getFilteredProducts(stepNum);

    // For step 1: process option groups sequentially, updating active state as we go
    if (stepNum === 1) {
      const groups = container.querySelectorAll('.configurator-attribute-group');

      groups.forEach((group, groupIndex) => {
        // Get fresh active options after previous groups may have changed
        const activeOptions = this.getActiveOptions(container);

        group.querySelectorAll('.configurator-attribute-button').forEach((btn) => {
          const attr = btn.dataset.attr;
          const val = btn.dataset.value;
          const optionIndex = parseInt(btn.dataset.optionIndex ?? -1);

          const { exists, available } = this.checkOptionAvailability(products, activeOptions, attr, val, stepNum);

          btn.dataset.exists = exists;
          btn.dataset.available = available;

          const shouldHide = config.hideNonExistentCombos(optionIndex) ? !exists : false;

          btn.style.display = shouldHide ? 'none' : '';
          btn.style.opacity = config.greyOutUnavailable && !available ? '0.5' : '1';
        });

        // Ensure this group has a visible active button before moving to next
        this.ensureGroupHasActiveButton(group);
      });
    } else {
      // For other steps: use original logic
      const activeOptions = this.getActiveOptions(container);

      container.querySelectorAll('.configurator-attribute-button').forEach((btn) => {
        const attr = btn.dataset.attr;
        const val = btn.dataset.value;
        const optionIndex = parseInt(btn.dataset.optionIndex ?? -1);

        // Quality buttons always visible
        if (attr === 'quality') {
          btn.style.display = '';
          btn.style.opacity = '1';
          return;
        }

        // "None" buttons always visible
        if (attr === 'product' && val === 'none') {
          btn.style.display = '';
          btn.style.opacity = '1';
          return;
        }

        const { exists, available } = this.checkOptionAvailability(products, activeOptions, attr, val, stepNum);

        btn.dataset.exists = exists;
        btn.dataset.available = available;

        // Visibility logic
        const shouldHide = config.hideUnavailable ? !available : config.hideNonExistentCombos(optionIndex) ? !exists : false;

        btn.style.display = shouldHide ? 'none' : '';
        btn.style.opacity = config.greyOutUnavailable && !available ? '0.5' : '1';
      });

      this.ensureActiveButtonVisible(container);
    }

    const finalOptions = this.getActiveOptions(container);
    this.updateNoProductsMessage(stepNum, products.length > 0);
  }

  ensureGroupHasActiveButton(group) {
    const activeBtn = group.querySelector('.configurator-attribute-button.active');

    if (activeBtn && activeBtn.style.display !== 'none') return;

    if (activeBtn) activeBtn.classList.remove('active');

    const newActive =
      Array.from(group.querySelectorAll('.configurator-attribute-button')).find((b) => b.dataset.available === 'true' && b.style.display !== 'none') ||
      Array.from(group.querySelectorAll('.configurator-attribute-button')).find((b) => b.style.display !== 'none');

    if (newActive) newActive.classList.add('active');
  }

  checkOptionAvailability(products, activeOptions, attr, value, stepNum) {
    const contextProducts = activeOptions.product ? products.filter((p) => p.id == activeOptions.product) : products;

    if (attr === 'product') {
      const exists = products.some((p) => p.id == value);
      return { exists, available: exists };
    }

    // For step 1: check each option group against only prior option groups
    // This prevents circular dependencies where later options affect earlier ones
    if (stepNum === 1) {
      const optionIndex = this.getOptionIndex(contextProducts[0], attr);
      const priorOptions = this.getPriorOptions(activeOptions, contextProducts[0], optionIndex);
      const testOptions = { ...priorOptions, [attr]: value };

      let exists = false;
      let available = false;

      for (const product of contextProducts) {
        for (const variant of product.variants) {
          if (this.variantMatchesOptions(product, variant, testOptions)) {
            exists = true;
            if (variant.available) {
              available = true;
              break;
            }
          }
        }
        if (available) break;
      }

      return { exists, available };
    }

    // For other steps: check against all active options
    const testOptions = { ...activeOptions, [attr]: value };
    let exists = false;
    let available = false;

    for (const product of contextProducts) {
      for (const variant of product.variants) {
        if (!this.matchesSizeFilter(product, variant) && stepNum > 1) continue;
        if (this.variantMatchesOptions(product, variant, testOptions)) {
          exists = true;
          if (variant.available) {
            available = true;
            break;
          }
        }
      }
      if (available) break;
    }

    return { exists, available };
  }

  getOptionIndex(product, attr) {
    if (!product) return -1;
    const name = attr === 'size' ? product.options.find((o) => SIZE_OPTION_REGEX.test(o)) : attr;
    return product.options.indexOf(name);
  }

  getPriorOptions(activeOptions, product, upToIndex) {
    if (!product || upToIndex <= 0) return {};
    const prior = {};
    for (let i = 0; i < upToIndex; i++) {
      const optName = product.options[i];
      const key = SIZE_OPTION_REGEX.test(optName) ? 'size' : optName;
      if (activeOptions[key] !== undefined) {
        prior[key] = activeOptions[key];
      }
    }
    return prior;
  }

  getActiveOptions(container) {
    const options = {};
    container.querySelectorAll('.configurator-attribute-button.active, .configurator-none-button.active').forEach((btn) => {
      options[btn.dataset.attr] = btn.dataset.value;
    });
    return options;
  }

  ensureActiveButtonVisible(container) {
    container.querySelectorAll('.configurator-attribute-group').forEach((group) => {
      const activeBtn = group.querySelector('.configurator-attribute-button.active');

      if (activeBtn && activeBtn.style.display !== 'none') return;

      if (activeBtn) activeBtn.classList.remove('active');

      const newActive =
        Array.from(group.querySelectorAll('.configurator-attribute-button')).find((b) => b.dataset.available === 'true' && b.style.display !== 'none') ||
        Array.from(group.querySelectorAll('.configurator-attribute-button')).find((b) => b.style.display !== 'none');

      if (newActive) newActive.classList.add('active');
    });
  }

  updateNoProductsMessage(stepNum, hasProducts) {
    const msg = this.querySelector(`[data-no-products-message="${stepNum}"]`);
    const container = this.querySelector(`[data-step-attributes="${stepNum}"]`);
    if (msg) msg.style.display = hasProducts ? 'none' : '';
    if (container) container.style.display = hasProducts ? '' : 'none';
  }

  syncButtonsToSelection(stepNum) {
    const selection = this.state.selections[stepNum];
    const container = this.querySelector(`[data-step-attributes="${stepNum}"]`);
    if (!container) return;

    // Handle "none" selection
    if (selection === null) {
      container.querySelectorAll('.configurator-attribute-button, .configurator-none-button').forEach((btn) => {
        btn.classList.remove('active');
      });
      const noneBtn = container.querySelector('.configurator-none-button[data-value="none"]');
      if (noneBtn) noneBtn.classList.add('active');
      return;
    }

    if (!selection) return;

    const match = this.findVariantById(stepNum, selection.variantId);
    if (!match) return;

    const { product, variant } = match;
    const opts = this.getVariantOptions(product, variant);
    const size = this.extractSize(product, variant);

    if (stepNum === 2 && product.pillowcase_quality?.length) {
      const productQuality = product.pillowcase_quality[0];
      if (this.state.qualityFilter !== productQuality) {
        this.state.qualityFilter = productQuality;
        this.renderStep(this.getStep(stepNum));
        this.injectInfoIcons();
      }
    }

    const updatedContainer = this.querySelector(`[data-step-attributes="${stepNum}"]`);
    if (!updatedContainer) return;

    updatedContainer.querySelectorAll('.configurator-attribute-button, .configurator-none-button').forEach((btn) => {
      btn.classList.remove('active');
      const attr = btn.dataset.attr;
      const val = btn.dataset.value;

      if (attr === 'product' && val == product.id) btn.classList.add('active');
      else if (attr === 'size' && this.sizesMatch(val, size)) btn.classList.add('active');
      else if (attr === 'quality' && val === this.state.qualityFilter) btn.classList.add('active');
      else if (opts[attr] === val) btn.classList.add('active');
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Selection Management
  // ─────────────────────────────────────────────────────────────

  selectFromActiveButtons(stepNum, skipButtonUpdate = false) {
    // If already updating from handleOptionClick, skip to avoid duplicate work
    if (this._isUpdating) return;

    const container = this.querySelector(`[data-step-attributes="${stepNum}"]`);
    if (!container) return;

    this._isUpdating = true;
    try {
      if (!skipButtonUpdate) this.updateButtonStates(stepNum);

      const options = this.getActiveOptions(container);

      const match = this.findMatchingVariant(stepNum, options);

      if (match) {
        this.state.selections[stepNum] = this.createSelection(match);
        if (stepNum === 1) this.onStep1SelectionChange();
      }

      this.persistToURL();
      this.updateUI();
    } finally {
      this._isUpdating = false;
    }
  }

  findMatchingVariant(stepNum, options) {
    let products = this.getFilteredProducts(stepNum);
    if (options.product) products = products.filter((p) => p.id == options.product);

    let bestMatch = null;

    for (const product of products) {
      for (const variant of product.variants) {
        if (stepNum > 1 && !this.matchesSizeFilter(product, variant)) continue;
        if (this.variantMatchesOptions(product, variant, options)) {
          if (variant.available) return { product, variant };
          if (!bestMatch) bestMatch = { product, variant };
        }
      }
    }

    return bestMatch;
  }

  onStep1SelectionChange() {
    this.state.qualityFilter = null;

    this.config.steps.forEach((step) => {
      if (step.number > 1) {
        delete this.state.selections[step.number];
        this.renderStep(step);
      }
    });

    this.injectInfoIcons();
  }

  attachListeners() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.configurator-attribute-button, .configurator-none-button');
      if (btn && !btn.disabled) {
        e.preventDefault();
        this.handleOptionClick(btn);
        return;
      }

      if (e.target.closest('[data-add-all-to-cart]')) {
        this.addAllToCart();
        return;
      }

      const navBtn = e.target.closest('[data-step-nav]');
      if (navBtn) {
        e.preventDefault();
        this.goToStep(parseInt(navBtn.dataset.gotoStep));
        return;
      }

      const header = e.target.closest('[data-step-header]');
      if (header) {
        e.preventDefault();
        this.goToStep(parseInt(header.dataset.stepHeader));
        return;
      }
    });
  }

  handleOptionClick(btn) {
    if (this._isUpdating) return;

    const stepNum = parseInt(btn.dataset.step);
    const attr = btn.dataset.attr;
    const val = btn.dataset.value;

    if (attr === 'quality') {
      this.state.qualityFilter = btn.dataset.value;
      delete this.state.selections[stepNum];
      this.renderStep(this.getStep(stepNum));
      this.injectInfoIcons();
      this.selectFromActiveButtons(stepNum);

      this.dispatchTrackingEvent('bundle:interaction', {
        type: this._hasTrackedStart ? 'updated' : 'started',
      });
      this._hasTrackedStart = true;
      return;
    }

    // Handle "none" selection
    if (attr === 'product' && val === 'none') {
      const container = this.querySelector(`[data-step-attributes="${stepNum}"]`);
      // Clear all active buttons in this step
      container?.querySelectorAll('.configurator-attribute-button.active').forEach((b) => b.classList.remove('active'));
      // Set only the "none" button as active
      btn.classList.add('active');
      this.state.selections[stepNum] = null;
      this.persistToURL();
      this.updateUI();

      this.dispatchTrackingEvent('bundle:interaction', {
        type: this._hasTrackedStart ? 'updated' : 'started',
      });
      this._hasTrackedStart = true;
      return;
    }

    this._isUpdating = true;
    try {
      const container = this.querySelector(`[data-step-attributes="${stepNum}"]`);

      // 1. Update the clicked button's active state
      btn.parentElement.querySelectorAll('.active').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');

      // Also deactivate "none" button if a product option is selected
      const noneBtn = container?.querySelector('[data-value="none"]');
      if (noneBtn) noneBtn.classList.remove('active');

      // 2. Update button availability based on the new selection
      this.updateButtonStates(stepNum);

      // 3. Now read the final active options and find matching variant
      const options = this.getActiveOptions(container);

      const match = this.findMatchingVariant(stepNum, options);

      if (match) {
        this.state.selections[stepNum] = this.createSelection(match);
        if (stepNum === 1) this.onStep1SelectionChange();

        this.dispatchTrackingEvent('bundle:interaction', {
          type: this._hasTrackedStart ? 'updated' : 'started',
        });
        this._hasTrackedStart = true;
      }

      this.persistToURL();
      this.updateUI();
    } finally {
      this._isUpdating = false;
    }
  }

  goToStep(n) {
    if (!this.querySelector(`[data-step="${n}"]`)) return;

    const previousStep = this.state.currentStep;

    if (n > previousStep && this.state.selections[previousStep] !== undefined) {
      this.dispatchTrackingEvent('bundle:step-completed', { step: previousStep });
    }

    this.state.currentStep = n;
    this.openStep(n);

    // Only auto-select if there's no selection at all (undefined), not if "none" is selected (null)
    if (this.state.selections[n] === undefined) this.selectFromActiveButtons(n);

    this.updateUI();
    this.persistToURL();
  }

  openStep(n) {
    this.querySelectorAll('[data-step]').forEach((el) => {
      const stepNum = parseInt(el.dataset.step);
      const header = el.querySelector('[data-step-header]');
      const isTarget = stepNum === n;

      el.classList.toggle('is-open', isTarget);
      header?.setAttribute('aria-expanded', isTarget);
    });
  }

  updateUI() {
    this.updateSummary();
    this.updateStepStates();
    this.updateImages();
    this.updateActionButtons();
    this.updateBundleOverview();
  }

  updateStepStates() {
    this.querySelectorAll('[data-step]').forEach((el) => {
      const stepNum = parseInt(el.dataset.step);
      const hasSelection = this.state.selections[stepNum] !== undefined;
      const isOpen = el.classList.contains('is-open');
      el.classList.toggle('is-selected', hasSelection && !isOpen);
    });

    if (this.elements.progress) this.elements.progress.value = this.state.currentStep;
  }

  updateImages() {
    const images = this.elements.images;
    if (!images) return;

    const selection = this.state.selections[this.state.currentStep];
    let match = null;
    let productId = null;

    // If there's a valid selection, use it
    if (selection && selection !== null) {
      match = this.findVariantById(this.state.currentStep, selection.variantId);
      productId = selection.productId;
    }

    // If no selection or no match found, use first available product as default
    if (!match) {
      const step = this.getStep(this.state.currentStep);
      const products = this.getFilteredProducts(this.state.currentStep);
      const firstProduct = products?.[0];

      if (firstProduct?.variants?.length && firstProduct?.images?.length) {
        match = { product: firstProduct, variant: firstProduct.variants[0] };
        productId = 'default-' + firstProduct.id; // Use a special ID to track default images
      }
    }

    // If still no match, keep showing whatever images were already displayed
    // (e.g. the step 1 product) instead of blanking out the whole media
    // column. Only hide it if nothing has ever been shown yet.
    if (!match?.product?.images?.length) {
      if (!this._lastImageProductId) images.style.display = 'none';
      return;
    }

    // Skip if already showing these images
    if (this._lastImageProductId === productId) return;
    this._lastImageProductId = productId;

    const newGroup = document.createElement('div');
    newGroup.className = 'pillow-configurator__images-group';
    newGroup.innerHTML = match.product.images
      .map(
        (img) => `
      <a class="pillow-configurator__image-link" href="${img.src}" data-fancybox="pillow-configurator-images">
        <img src="${img.src}" alt="${img.alt || ''}" width="${img.width}" height="${img.height}" loading="lazy">
      </a>`
      )
      .join('');

    images.appendChild(newGroup);
    newGroup.offsetHeight; // Force reflow

    requestAnimationFrame(() => {
      images.querySelectorAll('.pillow-configurator__images-group.active').forEach((g) => g.classList.remove('active'));
      newGroup.classList.add('active');
      setTimeout(() => {
        images.querySelectorAll('.pillow-configurator__images-group:not(.active)').forEach((g) => g.remove());
      }, 500);

      // Reinitialize Fancybox for new images
      this.initFancybox();
    });

    images.style.display = '';
  }

  initFancybox() {
    Fancybox.destroy();
    Fancybox.bind('[data-fancybox="pillow-configurator-images"]', {
      animated: false,
      showClass: false,
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Tracking Events (Klaviyo integration)
  // ─────────────────────────────────────────────────────────────

  dispatchTrackingEvent(eventName, detail = {}) {
    this.dispatchEvent(
      new CustomEvent(eventName, {
        bubbles: true,
        detail: {
          ...detail,
          state: { ...this.state },
          config: { steps: this.config.steps, title: this.config.title },
          selections: { ...this.state.selections },
        },
      })
    );
  }

  updateActionButtons() {
    const totalSteps = this.config.steps.length;

    // Show/hide step navigation buttons based on current step and selections
    this.querySelectorAll('[data-step-nav]').forEach((btn) => {
      const forStep = parseInt(btn.dataset.stepNav);
      const hasSelection = this.state.selections[forStep] !== undefined;
      const isCurrentStep = forStep === this.state.currentStep;
      const gotoStep = parseInt(btn.dataset.gotoStep);
      const nextStepHasSelection = this.state.selections[gotoStep] !== undefined;
      const shouldShow = isCurrentStep && hasSelection && !nextStepHasSelection;
      btn.style.display = shouldShow ? '' : 'none';
    });

    const addBtn = this.elements.addAllBtn;
    if (!addBtn) return;

    // Check if all steps have selections (including 'none' selections which are stored as null)
    const allStepsCompleted = this.config.steps.every((step) => this.state.selections[step.number] !== undefined);

    // Check that step 1 has an actual product (not 'none')
    const hasStep1 = this.state.selections[1] !== null && this.state.selections[1] !== undefined;

    // Get actual product selections (excluding 'none' selections)
    const actualSelections = Object.values(this.state.selections).filter((s) => s !== null);
    const hasSelections = actualSelections.length > 0;
    const allAvailable = actualSelections.every((s) => s.available !== false);

    // Show add to cart only if all steps are completed
    addBtn.style.display = allStepsCompleted ? '' : 'none';

    const isValid = hasStep1 && hasSelections && allAvailable;
    addBtn.disabled = !isValid;
    addBtn.title = !hasStep1 ? 'Please select a pillow' : !allAvailable ? window.pillowConfigurator?.selected_items_unavailable || '' : '';
  }

  updateBundleOverview() {
    const container = this.elements.bundleOverview;
    if (!container) return;

    // Filter out null (none) selections
    const actualSelections = Object.entries(this.state.selections)
      .filter(([_, sel]) => sel !== null)
      .map(([stepNum, sel]) => ({ stepNum: parseInt(stepNum), ...sel }));

    if (actualSelections.length === 0) {
      container.style.display = 'none';
      return;
    }

    const items = actualSelections
      .map((sel) => {
        const step = this.getStep(sel.stepNum);
        return `
        <div class="bundle-overview__item">
          ${sel.image ? `<img src="${sel.image}" alt="${sel.title}" class="bundle-overview__image" loading="lazy">` : ''}
          <div class="bundle-overview__details">
          <p class="bundle-overview__title">${sel.title}</p>
            ${sel.variantTitle !== 'Default Title' ? `<p class="bundle-overview__variant">${sel.variantTitle}</p>` : ''}
          </div>
        </div>`;
      })
      .join('');

    container.innerHTML = items;
    container.style.display = '';
  }

  updateSummary() {
    const el = this.elements.total;
    if (!el) return;

    let total = 0;
    let compareTotal = 0;

    // Filter out null (none) selections
    const actualSelections = Object.values(this.state.selections).filter((sel) => sel !== null);

    actualSelections.forEach((sel) => {
      total += sel.price;
      compareTotal += sel.compareAtPrice || sel.price;
    });

    const count = actualSelections.length;
    const settings = window.pillowConfiguratorSettings || {};
    const discount = count >= 3 ? settings.discount3 || 0 : count === 2 ? settings.discount2 || 0 : 0;

    const format = (cents) => formatMoney(cents, settings.moneyFormat || '${{amount}}');

    let displayTotal = total;
    let compareHtml = '';

    let savingsHtml = '';

    if (discount > 0) {
      displayTotal = total * (1 - discount / 100);
      compareHtml = `<span class="pillow-configurator__compare-price">${format(total)}</span>`;
      const savingsText = settings.savingsLabel || 'Bespaar {discount}%';
      savingsHtml = `<span class="pillow-configurator__savings-label">${savingsText.replace('{discount}', discount)}</span>`;
    } else if (compareTotal > total) {
      compareHtml = `<span class="pillow-configurator__compare-price">${format(compareTotal)}</span>`;
    }

    el.innerHTML = `<strong>${window.pillowConfiguratorSettings?.your_bundle}</strong> <span>${compareHtml}${format(displayTotal)}${savingsHtml}</span>`;
    el.style.display = '';
  }

  async addAllToCart() {
    // Filter out null (none) selections
    const actualSelections = Object.values(this.state.selections).filter((s) => s !== null);
    if (actualSelections.some((s) => !s.available)) {
      alert('Some items are sold out.');
      return;
    }

    const addBtn = this.elements.addAllBtn;
    addBtn.classList.add('loading');
    addBtn.disabled = true;

    try {
      // Only include steps that have actual selections (not null or undefined)
      const steps = Object.keys(this.state.selections)
        .filter((step) => this.state.selections[step] !== null && this.state.selections[step] !== undefined)
        .sort((a, b) => a - b);

      if (steps.length === 0) {
        alert('Please select at least one product.');
        addBtn.classList.remove('loading');
        addBtn.disabled = false;
        return;
      }

      const parentId = this.state.selections[steps[0]].variantId;
      const bundleId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

      const items = steps.map((step, i) => ({
        id: this.state.selections[step].variantId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_handle: this.config.handle,
          ...(i > 0 && { _can_update_quantity: false }),
        },
        ...(i > 0 && { parent_id: parentId }),
      }));

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description);
      }

      window.location.href = '/cart';
    } catch (e) {
      console.error(e);
      alert(e.message || 'Error adding to cart');
      addBtn.classList.remove('loading');
      addBtn.disabled = false;
    }
  }
}

customElements.define('pillow-configurator', PillowConfigurator);
